import type { SystemConfig } from '@cabgo/shared';
import { Spinner } from '@cabgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../lib/api';

/**
 * Public /legal/terms and /legal/privacy (no login). The mobile apps link here
 * via `FlavorConfig.legalTermsUrl/legalPrivacyUrl` (${appBaseUrl}/legal/…), so
 * this page is the stable address; where it FORWARDS is backend-configured
 * (System Settings → legal URLs) — point it at a CMS/WordPress later without
 * shipping new app builds. Unconfigured → a placeholder page (never a 404;
 * store review probes these links).
 */
export function LegalRedirectPage({ kind }: { kind: 'terms' | 'privacy' }) {
  const { t } = useTranslation('common');
  const { data, isLoading } = useQuery({
    queryKey: ['system-config'],
    // Plain fetch: this page must work logged-out (no auth header machinery).
    queryFn: async (): Promise<SystemConfig> => {
      const res = await fetch(`${API_URL}/api/v1/system-config`);
      if (!res.ok) throw new Error(`system-config ${res.status}`);
      return (await res.json()) as SystemConfig;
    },
    staleTime: 60_000,
  });

  const raw = kind === 'terms' ? data?.legalTermsUrl : data?.legalPrivacyUrl;
  // Only ever forward to http(s) — the API validates on write too, but a
  // javascript:/data: URL must never reach location.replace (XSS).
  const target = raw && /^https?:\/\//i.test(raw) ? raw : undefined;
  useEffect(() => {
    // Navigation is a side effect — never run it during render.
    if (target) window.location.replace(target);
  }, [target]);

  return (
    <div className="cg-center" style={{ minHeight: '100vh', flexDirection: 'column', gap: 12, padding: 24 }}>
      {isLoading || target ? (
        <Spinner />
      ) : (
        <>
          <h1 style={{ fontSize: 20, color: 'var(--text-strong)' }}>
            {t(kind === 'terms' ? 'legalTermsTitle' : 'legalPrivacyTitle')}
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: 480, textAlign: 'center' }}>
            {t('legalPlaceholder')}
          </p>
        </>
      )}
    </div>
  );
}
